import { Head, Link, router } from '@inertiajs/react'
import { DollarSign } from 'lucide-react'
import MainLayout from '@/components/Layout/MainLayout'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import type { CurrentUser } from '@/types/shared'

interface CallerRow { caller_name: string; calls: number; cost_cents: number }
interface ModelRow { model: string; calls: number; cost_cents: number; total_tokens: number }
interface AccountRow { account_id: string | null; name: string; slug: string | null; calls: number; cost_cents: number }
interface DailyRow { date: string; cost_cents: number }

interface Props {
  current_user: CurrentUser
  god_mode: boolean
  window: string
  windows: string[]
  totals: { calls: number; input_tokens: number; output_tokens: number; cached_tokens: number; cost_cents: number }
  by_caller: CallerRow[]
  by_model: ModelRow[]
  by_account: AccountRow[]
  daily: DailyRow[]
}

const dollars = (c: number) => `$${(c / 100).toFixed(2)}`
const num = (n: number) => n.toLocaleString()

export default function AICosts({ current_user, window, windows, totals, by_caller, by_model, by_account, daily }: Props) {
  const switchWindow = (w: string) => router.get('/god-mode/ai-costs', { window: w }, { preserveScroll: true })
  const maxDaily = Math.max(1, ...daily.map(d => d.cost_cents))

  return (
    <>
      <Head title="God Mode · AI Costs" />
      <MainLayout user={current_user} godMode breadcrumbs={[{ label: 'God Mode' }, { label: 'AI Costs' }]}>
        <div className="p-6 max-w-6xl mx-auto space-y-6">
          <header className="flex items-end justify-between">
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <DollarSign className="size-6 text-primary" /> AI cost dashboard
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Aggregated AIUsageLog across all accounts.</p>
            </div>
            <div className="flex gap-1 bg-muted/30 rounded-md p-1">
              {windows.map(w => (
                <Button key={w} size="sm" variant={w === window ? 'default' : 'ghost'}
                        className="cursor-pointer" onClick={() => switchWindow(w)}>
                  {w}
                </Button>
              ))}
            </div>
          </header>

          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Total cost" value={dollars(totals.cost_cents)} />
            <Stat label="API calls" value={num(totals.calls)} />
            <Stat label="Input tokens" value={num(totals.input_tokens)} />
            <Stat label="Output tokens" value={num(totals.output_tokens)} />
            <Stat label="Cached tokens" value={num(totals.cached_tokens)} />
          </div>

          {/* Daily sparkline */}
          {daily.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Daily spend</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-1 items-end h-24">
                  {daily.map(d => (
                    <div key={d.date} className="flex-1 group relative" title={`${d.date}: ${dollars(d.cost_cents)}`}>
                      <div className="bg-primary/60 hover:bg-primary rounded-t"
                           style={{ height: `${(d.cost_cents / maxDaily) * 100}%`, minHeight: d.cost_cents > 0 ? '2px' : '0' }} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BreakdownCard title="By caller" rows={by_caller.map(r => ({ label: r.caller_name, calls: r.calls, cost_cents: r.cost_cents }))} />
            <BreakdownCard title="By model" rows={by_model.map(r => ({ label: r.model, calls: r.calls, cost_cents: r.cost_cents, sub: `${num(r.total_tokens)} tok` }))} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top accounts</CardTitle>
              <CardDescription>Top 20 by spend in this window</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {by_account.map(a => (
                  <div key={a.account_id ?? a.name} className="grid grid-cols-[1fr_120px_120px] gap-3 items-center px-4 py-2.5 text-sm hover:bg-muted/30">
                    <div>
                      {a.slug ? (
                        <Link href={`/god-mode/accounts/${a.account_id}`} className="hover:underline">
                          {a.name} <span className="text-muted-foreground font-mono text-xs">/{a.slug}</span>
                        </Link>
                      ) : a.name}
                    </div>
                    <div className="font-mono text-right text-muted-foreground">{num(a.calls)} calls</div>
                    <div className="font-mono text-right">{dollars(a.cost_cents)}</div>
                  </div>
                ))}
                {by_account.length === 0 && (
                  <div className="px-6 py-10 text-center text-sm text-muted-foreground">No usage in this window.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    </>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="text-xl font-semibold text-primary">{value}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      </CardContent>
    </Card>
  )
}

function BreakdownCard({ title, rows }: { title: string; rows: { label: string; calls: number; cost_cents: number; sub?: string }[] }) {
  const total = rows.reduce((s, r) => s + r.cost_cents, 0)
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-border">
          {rows.map(r => (
            <div key={r.label} className="px-4 py-2.5 text-sm">
              <div className="flex justify-between gap-3">
                <span className="truncate">{r.label}</span>
                <span className="font-mono shrink-0">{dollars(r.cost_cents)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>{r.calls} calls{r.sub ? ` · ${r.sub}` : ''}</span>
                {total > 0 && <span>{Math.round((r.cost_cents / total) * 100)}%</span>}
              </div>
              {total > 0 && (
                <div className="h-1 bg-muted rounded mt-1 overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${(r.cost_cents / total) * 100}%` }} />
                </div>
              )}
            </div>
          ))}
          {rows.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">No data.</div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
